import { useCallback, useMemo, useState } from 'react';
import { Button } from 'rizzui';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import AttivaCard from '../../../components/card/AttivaCard';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import PageHeader from '../../../layout/shared/page-header';
import { menuRoutes } from '../../../router/menu';
import { DepartmentService } from '../../../services/department/departmentService';
import { InfoCard, InfoField, InfoGroup, formatInfoDate, formatInfoValue } from '../../contracts/profile/views/info/components/InfoDisplay';
import DepartmentForm from '../DepartmentForm';
import { CopyConfigModal } from '../list/components/CopyConfigModal';
import { InfoModal } from '../list/components/InfoModal';

const breadCrumb = [
    { name: 'Administración' },
    { name: 'Redes comerciales y departamentos', href: menuRoutes.networks.path },
    { name: 'Detalle de departamento' },
];

type ViewMode = 'summary' | 'edit';

const tabStyles = {
    container: 'max-w-[99vw] sticky z-20 -mx-4 -mt-4 border-b border-muted bg-white px-4 py-0 font-medium text-gray-500 md:-mx-5 md:px-5 lg:-mx-8 lg:mt-0 lg:px-8 xl:-mx-6 xl:px-6 3xl:-mx-[33px] 3xl:px-[33px] 4xl:-mx-10 4xl:px-10 top-[62px] md:top-[71px]',
    list: '-mb-7 flex w-full gap-3 overflow-x-auto scroll-smooth pb-7 md:gap-5 lg:gap-8',
    tab: 'group relative cursor-pointer whitespace-nowrap py-2.5 font-medium text-gray-500',
    active: 'text-primary before:visible before:w-full before:opacity-100',
    inactive: 'before:invisible before:w-0 before:opacity-0 hover:text-gray-900',
};

const DepartmentDetailPage = () => {
    const navigate = useNavigate();
    const { userCan } = usePrivilege();
    const { id } = useParams();
    const [viewMode, setViewMode] = useState<ViewMode>('summary');
    const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);
    const [copyModalOpen, setCopyModalOpen] = useState<boolean>(false);

    const [department, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return { success: false, data: null, message: 'missing-id' };
        const response = await (new DepartmentService()).getDepartmentById(id);
        return response.getResponseData();
    }, [id]));

    const networks = useMemo(() => {
        return (department?.commercialNetworks ?? [])
            .map((item: any) => item?.commercialNetwork ?? item)
            .filter((item: any) => item?.id && item?.name)
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
    }, [department]);

    const users = department?.user ?? department?.users ?? [];
    const productTypes = department?.departmentsHasProductTypes ?? [];
    const viewStates = (department?.departmentsHasStatuses ?? []).filter((state: any) => state.action === 'view');
    const editStates = (department?.departmentsHasStatuses ?? []).filter((state: any) => state.action === 'edit');
    const dashboardStates = (department?.departmentsHasStatuses ?? []).filter((state: any) => state.action === 'dashboard');
    const canEditDepartment = userCan('edit_departments', 'departments');

    const infoNodeData = useMemo(() => ({
        id: department?.id,
        name: department?.name,
        users,
        usersCount: users.length,
        productTypes,
        productTypesCount: productTypes.length,
        viewStates,
        viewStatesCount: viewStates.length,
        editStates,
        editStatesCount: editStates.length,
        dashboardStates,
        dashboardStatesCount: dashboardStates.length,
    }), [department, users, productTypes, viewStates, editStates, dashboardStates]);

    return (
        <>
            <PageHeader title="Detalle de departamento" breadcrumb={breadCrumb}>
                <div className="flex items-center gap-2">
                    <Button variant="flat" size="sm" onClick={() => navigate(-1)}>
                        <IoArrowBackOutline className="me-2 h-4 w-4" />
                        Volver atrás
                    </Button>
                </div>
            </PageHeader>

            <div className={tabStyles.container}>
                <div className="relative flex items-center overflow-hidden">
                    <div className="flex h-[52px] items-start overflow-hidden w-full">
                        <div className={tabStyles.list}>
                            <button
                                type="button"
                                className={`${tabStyles.tab} before:absolute before:bottom-0 before:left-0 before:z-[1] before:h-0.5 before:bg-gray-1000 before:transition-all ${viewMode === 'summary' ? tabStyles.active : tabStyles.inactive}`}
                                onClick={() => setViewMode('summary')}
                            >
                                <span className="inline-flex rounded-md px-2.5 py-1.5 transition-all duration-200 group-hover:bg-gray-100/70">Información</span>
                            </button>
                            {canEditDepartment && (
                                <button
                                    type="button"
                                    className={`${tabStyles.tab} before:absolute before:bottom-0 before:left-0 before:z-[1] before:h-0.5 before:bg-gray-1000 before:transition-all ${viewMode === 'edit' ? tabStyles.active : tabStyles.inactive}`}
                                    onClick={() => setViewMode('edit')}
                                >
                                    <span className="inline-flex rounded-md px-2.5 py-1.5 transition-all duration-200 group-hover:bg-gray-100/70">Editar</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {loading && <Loader height={'70'} />}

            {!loading && (error || !department) && (
                <AttivaCard title="" className="text-sm text-gray-600">
                    No se ha podido cargar el departamento solicitado.
                </AttivaCard>
            )}

            {!loading && department && viewMode === 'summary' && (
                <div className="mt-3 flex flex-col gap-5 w-full transition-all duration-300 ease-in-out md:relative pe-2">
                    <InfoCard title="Datos del Departamento" subtitle="Información operativa, de configuración y relaciones del departamento.">
                        <InfoGroup title="Información General" description="Datos principales del departamento.">
                            <InfoField label="Nombre" value={formatInfoValue(department.name)} className="md:col-span-4" />
                            <InfoField label="Descripción" value={formatInfoValue(department.description)} className="md:col-span-8" />
                            <InfoField label="Creación" value={formatInfoDate(department.createdAt)} className="md:col-span-4" />
                            <InfoField label="Actualización" value={formatInfoDate(department.updatedAt)} className="md:col-span-4" />
                        </InfoGroup>

                        <InfoGroup title="Configuración" description="Resumen funcional de usuarios, productos y estados.">
                            <InfoField label="Nº usuarios" value={formatInfoValue(users.length)} className="md:col-span-3" />
                            <InfoField label="Nº tipos de producto" value={formatInfoValue(productTypes.length)} className="md:col-span-3" />
                            <InfoField label="Estados visibles configurados" value={formatInfoValue(viewStates.length)} className="md:col-span-3" />
                            <InfoField label="Estados editables configurados" value={formatInfoValue(editStates.length)} className="md:col-span-3" />
                            <InfoField label="Estados dashboard configurados" value={formatInfoValue(dashboardStates.length)} className="md:col-span-3" />
                        </InfoGroup>

                        <InfoGroup title="Redes Relacionadas" description="Redes comerciales asociadas al departamento.">
                            <InfoField
                                label="Listado de redes"
                                className="md:col-span-12"
                                value={networks.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                        {networks.map((network: any) => (
                                            <button
                                                key={network.id}
                                                type="button"
                                                className="text-left text-primary hover:underline"
                                                onClick={() => navigate(`${menuRoutes.networks.path}/detail/${network.id}`)}
                                            >
                                                {network.name}
                                            </button>
                                        ))}
                                    </div>
                                ) : 'N/A'}
                            />
                        </InfoGroup>

                        <InfoGroup title="Entidades Asociadas" description="Usuarios y tipos de producto vinculados al departamento.">
                            <InfoField
                                label="Usuarios"
                                value={users.length > 0 ? users.map((user: any) => `${user.name} ${user.lastName ?? ''}`.trim()).join(', ') : 'N/A'}
                                className="md:col-span-12"
                            />
                            <InfoField
                                label="Tipos de producto"
                                value={productTypes.length > 0 ? productTypes.map((item: any) => item?.productType?.name).filter(Boolean).join(', ') : 'N/A'}
                                className="md:col-span-12"
                            />
                        </InfoGroup>
                    </InfoCard>
                </div>
            )}

            {!loading && department && canEditDepartment && viewMode === 'edit' && (
                <div className="mt-3 flex flex-col gap-5 w-full transition-all duration-300 ease-in-out md:relative pe-2">
                    <AttivaCard title="Edición de Departamento" className="border-transparent lg:p-0">
                        <DepartmentForm
                            id={id}
                            setNodeId={() => { }}
                            setCopyConfig={setCopyModalOpen}
                            setInfo={setInfoModalOpen}
                            isOpen
                            setIsOpen={(value) => {
                                if (!value) navigate(-1);
                            }}
                            refetch={refetch}
                        />
                    </AttivaCard>

                    <InfoModal
                        isOpen={infoModalOpen}
                        nodeData={infoNodeData}
                        onClose={() => setInfoModalOpen(false)}
                    />

                    <CopyConfigModal
                        department={department}
                        isOpen={copyModalOpen}
                        onClose={() => setCopyModalOpen(false)}
                        refetch={refetch}
                    />
                </div>
            )}
        </>
    );
};

export default DepartmentDetailPage;
